import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Download,
  Info,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useDataProvider, useTranslate } from "ra-core";
import { useMemo, useState, type ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { useEsAdmin } from "../datos/useEsAdmin";
import {
  esAnaliticaNoDisponible,
  informeCsv,
  pctONull,
  textoDias,
  type MesInforme,
} from "../dominio/analitica";
import { FiltroPersonasSelect } from "../equipo/FiltroPersonasSelect";
import { useFiltroPersonas } from "../equipo/useFiltroPersonas";
import { formatoImporte } from "../negocios/formato";
import {
  cambio,
  cifrasMes,
  conclusionesMes,
  diasLaborables,
  esMesEnCurso,
  finDeMes,
  media,
  mesDeFecha,
  mesPorDefecto,
  moverMes,
  type CifrasMes,
  type Conclusion,
} from "../dominio/informe";
import type { DatosPanel } from "../dominio/panelInicio";
import { Canales, FiltroFuente, Perdidas } from "./PanelAnalisis";
import { Bloque, Td, Th } from "./piezas";

/** «Octubre de 2026». */
const nombreMes = (mes: string) => {
  const texto = new Date(`${mes.slice(0, 10)}T12:00:00`).toLocaleDateString(
    "es-ES",
    { month: "long", year: "numeric" },
  );
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};
const numero = (n: number | null) =>
  n == null ? "—" : n.toLocaleString("es-ES");
const pct = (n: number | null) =>
  n == null
    ? "—"
    : `${n.toLocaleString("es-ES", { maximumFractionDigits: 1 })} %`;

type Columna = {
  clave: keyof MesInforme | "tasa_contacto" | "tasa_cita" | "tasa_cliente";
  grupo: "leads" | "actividad" | "dinero";
  /** Valor para comparar con el mes anterior (null = sin flecha). */
  valor: (m: MesInforme) => number | null;
  texto: (m: MesInforme) => string;
  /** Subir es bueno (verde) o malo (perdidos, días hasta cerrar). */
  subirEsBueno?: boolean;
  flecha?: boolean;
  soloAdmin?: boolean;
};

const COLUMNAS: Columna[] = [
  {
    clave: "entrados",
    grupo: "leads",
    valor: (m) => m.entrados,
    texto: (m) => numero(m.entrados),
    flecha: true,
    subirEsBueno: true,
  },
  {
    clave: "tasa_contacto",
    grupo: "leads",
    valor: (m) => pctONull(m.contactados, m.entrados),
    texto: (m) => pct(pctONull(m.contactados, m.entrados)),
    flecha: true,
    subirEsBueno: true,
  },
  {
    clave: "tasa_cita",
    grupo: "leads",
    valor: (m) => pctONull(m.cita, m.entrados),
    texto: (m) => pct(pctONull(m.cita, m.entrados)),
    flecha: true,
    subirEsBueno: true,
  },
  {
    clave: "tasa_cliente",
    grupo: "leads",
    valor: (m) => pctONull(m.cliente, m.entrados),
    texto: (m) => pct(pctONull(m.cliente, m.entrados)),
    flecha: true,
    subirEsBueno: true,
  },
  {
    clave: "llamadas",
    grupo: "actividad",
    valor: (m) => m.llamadas,
    texto: (m) => numero(m.llamadas),
    flecha: true,
    subirEsBueno: true,
  },
  {
    clave: "contestadas",
    grupo: "actividad",
    valor: (m) => pctONull(m.contestadas, m.llamadas),
    texto: (m) => pct(pctONull(m.contestadas, m.llamadas)),
    flecha: true,
    subirEsBueno: true,
  },
  {
    clave: "citas",
    grupo: "actividad",
    valor: (m) => m.citas,
    texto: (m) => numero(m.citas),
    flecha: true,
    subirEsBueno: true,
  },
  {
    clave: "realizadas",
    grupo: "actividad",
    valor: (m) => m.realizadas,
    texto: (m) => numero(m.realizadas),
    flecha: true,
    subirEsBueno: true,
  },
  {
    clave: "clientes",
    grupo: "actividad",
    valor: (m) => m.clientes,
    texto: (m) => numero(m.clientes),
    flecha: true,
    subirEsBueno: true,
  },
  {
    clave: "perdidos",
    grupo: "actividad",
    valor: (m) => m.perdidos,
    texto: (m) => numero(m.perdidos),
    flecha: true,
    subirEsBueno: false,
  },
  {
    clave: "motivo_principal",
    grupo: "actividad",
    valor: () => null,
    texto: (m) => m.motivo_principal ?? "—",
  },
  {
    clave: "dias_ciclo",
    grupo: "actividad",
    valor: (m) => m.dias_ciclo,
    texto: (m) => textoDias(m.dias_ciclo),
    flecha: true,
    subirEsBueno: false,
  },
  {
    clave: "firmado",
    grupo: "dinero",
    valor: (m) => m.firmado,
    texto: (m) => (m.firmado == null ? "—" : formatoImporte(m.firmado)),
    flecha: true,
    subirEsBueno: true,
    soloAdmin: true,
  },
  {
    clave: "cobrado",
    grupo: "dinero",
    valor: (m) => m.cobrado,
    texto: (m) => (m.cobrado == null ? "—" : formatoImporte(m.cobrado)),
    flecha: true,
    subirEsBueno: true,
    soloAdmin: true,
  },
  {
    clave: "venta_media",
    grupo: "dinero",
    valor: (m) => m.venta_media,
    texto: (m) => (m.venta_media == null ? "—" : formatoImporte(m.venta_media)),
    soloAdmin: true,
  },
];

/**
 * Informe de cierre de mes (47; rehecho el 02/11/2026, Robert: «datos útiles, los típicos de
 * una empresa grande, para avanzar, mejorar y analizar»). Un mes, por defecto el último cerrado:
 * conclusiones escritas, resultados y actividad frente al mes anterior y a la media de los tres
 * anteriores, embudo de los leads del mes, equipo, por qué se perdió, canales y la tendencia de
 * 12 meses (con la tabla mes a mes y la descarga para Excel). El mes en curso no se compara.
 * Lo cuenta la base (nevora_informe_mensual, nevora_panel y nevora_panel_analisis); el setter ve
 * lo suyo y sin importes.
 */
export const InformeMensual = () => {
  const translate = useTranslate();
  const esAdmin = useEsAdmin();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const hoy = useMemo(() => new Date(), []);
  const [mes, setMes] = useState(() => mesPorDefecto(hoy));
  const [origen, setOrigen] = useState<string | null>(null);
  const [seleccion, setSeleccion] = useFiltroPersonas();
  const [verTabla, setVerTabla] = useState(false);
  const personas = useMemo(
    () =>
      esAdmin
        ? seleccion.map(Number).filter((n) => Number.isFinite(n) && n > 0)
        : [],
    [esAdmin, seleccion],
  );
  const t = (k: string, o?: object) => translate(`nevora.informe.${k}`, o);
  const hasta = finDeMes(mes);

  const informe = useQuery({
    queryKey: ["informe-mensual", MESES_HISTORIA, personas, origen],
    queryFn: () =>
      dataProvider.informeMensual(MESES_HISTORIA, personas, origen),
    retry: (n, e) => !esAnaliticaNoDisponible(e) && n < 2,
  });
  const panel = useQuery({
    queryKey: ["panel", mes, hasta, personas, origen],
    queryFn: () => dataProvider.panel(mes, hasta, personas, origen),
    retry: 1,
  });
  const analisis = useQuery({
    queryKey: ["panel-analisis", mes, hasta, personas, origen],
    queryFn: () => dataProvider.panelAnalisis(mes, hasta, personas, origen),
    retry: 1,
  });

  const filas = informe.data ?? [];
  const i = filas.findIndex((f) => f.mes.slice(0, 10) === mes);
  const fila = i >= 0 ? filas[i] : null;
  const enCurso = esMesEnCurso(mes, hoy);
  const actual = fila ? cifrasMes(fila) : null;
  const anterior = i > 0 ? cifrasMes(filas[i - 1]) : null;
  const tresAntes = filas
    .slice(Math.max(0, i - 3), Math.max(0, i))
    .map(cifrasMes);
  const primerMes = filas[0]?.mes.slice(0, 10) ?? mes;
  const mesActual = mesDeFecha(hoy);
  const noDisponible =
    !!informe.error && esAnaliticaNoDisponible(informe.error);

  const descargar = () => {
    if (!filas.length) return;
    const columnas = COLUMNAS.filter((c) => !c.soloAdmin || esAdmin);
    const csv = informeCsv(filas, [
      { clave: "mes", titulo: t("columnas.mes") },
      ...columnas.map((c) => ({
        clave: c.clave,
        titulo: t(`columnas.${c.clave}`),
      })),
    ]);
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const enlace = document.createElement("a");
    enlace.href = url;
    enlace.download = `nevora-informe-mensual-${filas[filas.length - 1].mes.slice(0, 7)}.csv`;
    enlace.click();
    URL.revokeObjectURL(url);
  };

  const comparar = (
    clave: keyof CifrasMes,
    tipo: "recuento" | "tasa",
  ): { anterior: ReturnType<typeof cambio>; media: number | null } => ({
    anterior:
      enCurso || !actual || !anterior
        ? null
        : cambio(
            actual[clave] as number | null,
            anterior[clave] as number | null,
            tipo,
          ),
    media: enCurso ? null : media(tresAntes, clave),
  });

  const cuerpo = (
    <div
      className="mx-auto max-w-[1400px] space-y-6 pb-12"
      data-testid="informe-mensual"
    >
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl space-y-1">
          <p className="etiqueta">{t("titulo")}</p>
          <h1 className="titulo-pagina">{nombreMes(mes)}</h1>
          <p className="text-sm text-muted-foreground">
            {enCurso ? t("en_curso_ayuda") : t("cerrado_ayuda")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center">
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={t("mes_anterior")}
              disabled={mes <= primerMes}
              onClick={() => setMes(moverMes(mes, -1))}
            >
              <ChevronLeft className="size-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="size-8"
              aria-label={t("mes_siguiente")}
              disabled={mes >= mesActual}
              onClick={() => setMes(moverMes(mes, 1))}
            >
              <ChevronRight className="size-4" />
            </Button>
          </div>
          <FiltroFuente valor={origen} cambiar={setOrigen} />
          <FiltroPersonasSelect valor={seleccion} cambiar={setSeleccion} />
          <Button
            variant="outline"
            className="gap-2"
            onClick={descargar}
            disabled={!filas.length}
          >
            <Download className="size-4" aria-hidden />
            {t("descargar")}
          </Button>
        </div>
      </header>

      {noDisponible ? (
        <p className="superficie p-8 text-center text-sm text-muted-foreground">
          {t("no_disponible")}
        </p>
      ) : !informe.data || !actual || !fila ? (
        informe.data && !fila ? (
          <p className="superficie p-8 text-center text-sm text-muted-foreground">
            {t("vacio")}
          </p>
        ) : (
          <Skeleton className="h-96 rounded-xl" />
        )
      ) : (
        <>
          <Bloque titulo={t("conclusiones.titulo")} testid="conclusiones">
            <Conclusiones
              frases={conclusionesMes({
                actual,
                anterior,
                enCurso,
                analisis: analisis.data ?? null,
                mesInforme: fila,
                t,
                esAdmin,
                nombreCanal: (c) =>
                  translate(`nevora.inicio.canales.tipos.${c}`),
                nombreDia: (d) => translate(`nevora.inicio.franjas.dias.${d}`),
              })}
            />
          </Bloque>

          <section className="space-y-3" aria-label={t("resultados")}>
            <h2 className="etiqueta">{t("resultados")}</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              <Indicador
                etiqueta={t("ind.clientes")}
                valor={numero(actual.clientes)}
                {...comparar("clientes", "recuento")}
                formatoMedia={decimal}
              />
              {esAdmin ? (
                <>
                  <Indicador
                    etiqueta={t("ind.firmado")}
                    valor={importe(actual.firmado)}
                    {...comparar("firmado", "recuento")}
                    formatoMedia={importe}
                  />
                  <Indicador
                    etiqueta={t("ind.cobrado")}
                    valor={importe(actual.cobrado)}
                    {...comparar("cobrado", "recuento")}
                    formatoMedia={importe}
                  />
                  <Indicador
                    etiqueta={t("ind.venta_media")}
                    valor={importe(actual.ventaMedia)}
                    {...comparar("ventaMedia", "recuento")}
                    formatoMedia={importe}
                  />
                </>
              ) : (
                <Indicador
                  etiqueta={t("ind.realizadas")}
                  valor={numero(actual.realizadas)}
                  {...comparar("realizadas", "recuento")}
                  formatoMedia={decimal}
                />
              )}
              <Indicador
                etiqueta={t("ind.cierre")}
                valor={porcentaje(actual.cierre)}
                detalle={t("ind.cierre_detalle")}
                {...comparar("cierre", "tasa")}
                formatoMedia={porcentaje}
              />
              <Indicador
                etiqueta={t("ind.ciclo")}
                valor={textoDias(actual.diasCiclo)}
                {...comparar("diasCiclo", "recuento")}
                subirEsBueno={false}
                formatoMedia={(n) => textoDias(n)}
              />
            </div>
          </section>

          <section className="space-y-3" aria-label={t("actividad")}>
            <h2 className="etiqueta">{t("actividad")}</h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              <Indicador
                etiqueta={t("ind.llamadas")}
                valor={numero(actual.llamadas)}
                {...comparar("llamadas", "recuento")}
                formatoMedia={decimal}
              />
              <Indicador
                etiqueta={t("ind.por_dia")}
                valor={decimal(
                  diasLaborables(mes, hoy)
                    ? actual.llamadas / diasLaborables(mes, hoy)
                    : null,
                )}
                detalle={t("ind.por_dia_detalle", {
                  n: diasLaborables(mes, hoy),
                })}
                anterior={null}
                media={null}
              />
              <Indicador
                etiqueta={t("ind.contacto")}
                valor={porcentaje(actual.contacto)}
                {...comparar("contacto", "tasa")}
                formatoMedia={porcentaje}
              />
              <Indicador
                etiqueta={t("ind.citas")}
                valor={numero(actual.citas)}
                {...comparar("citas", "recuento")}
                formatoMedia={decimal}
              />
              <Indicador
                etiqueta={t("ind.asistencia")}
                valor={porcentaje(actual.asistencia)}
                detalle={t("ind.asistencia_detalle", {
                  hechas: actual.realizadas,
                  no: fila.no_shows,
                })}
                {...comparar("asistencia", "tasa")}
                formatoMedia={porcentaje}
              />
              <Indicador
                etiqueta={t("ind.propuestas")}
                valor={numero(panel.data?.kpis.actual.propuestas ?? null)}
                anterior={null}
                media={null}
              />
            </div>
          </section>

          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <Bloque
              titulo={t("embudo.titulo")}
              ayuda={t("embudo.ayuda", { n: fila.entrados })}
            >
              <EmbudoMes fila={fila} />
            </Bloque>
            <Bloque
              titulo={translate("nevora.inicio.canales.titulo")}
              ayuda={t("canales_ayuda")}
            >
              {analisis.data ? (
                <Canales datos={analisis.data} />
              ) : (
                <Skeleton className="h-40" />
              )}
            </Bloque>
          </div>

          <Bloque
            titulo={translate("nevora.inicio.perdidas.titulo")}
            ayuda={t("perdidas_ayuda", { n: fila.perdidos })}
          >
            {analisis.data ? (
              <Perdidas datos={analisis.data} />
            ) : (
              <Skeleton className="h-40" />
            )}
          </Bloque>

          {esAdmin && panel.data ? (
            <Bloque titulo={t("equipo.titulo")} ayuda={t("equipo.ayuda")}>
              <EquipoMes
                ranking={panel.data.ranking}
                consultores={panel.data.consultores}
                dias={diasLaborables(mes, hoy)}
              />
            </Bloque>
          ) : null}

          <Bloque
            titulo={t("tendencia.titulo")}
            ayuda={t("tendencia.ayuda")}
            accion={
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setVerTabla(!verTabla)}
                aria-expanded={verTabla}
              >
                {verTabla
                  ? t("tendencia.ocultar_tabla")
                  : t("tendencia.ver_tabla")}
              </Button>
            }
          >
            <Tendencia
              filas={filas.slice(Math.max(0, i - 11), i + 1)}
              mes={mes}
              esAdmin={esAdmin}
            />
            {verTabla ? (
              <TablaMesAMes
                filas={[...filas.slice(0, i + 1)].reverse()}
                esAdmin={esAdmin}
              />
            ) : null}
          </Bloque>
        </>
      )}
    </div>
  );

  if (isMobile)
    return (
      <>
        <MobileHeader>
          <h1 className="titulo-seccion">{t("titulo")}</h1>
        </MobileHeader>
        <MobileContent>{cuerpo}</MobileContent>
      </>
    );
  return <div className="mt-2">{cuerpo}</div>;
};

InformeMensual.path = "/informe";

const MESES_HISTORIA = 24;
const decimal = (n: number | null) =>
  n == null ? "—" : n.toLocaleString("es-ES", { maximumFractionDigits: 1 });
const porcentaje = (n: number | null) =>
  n == null ? "—" : `${Math.round(n)} %`;
const importe = (n: number | null) => (n == null ? "—" : formatoImporte(n));

/** Una cifra del mes con su comparación (mes anterior y media de los tres anteriores). */
const Indicador = ({
  etiqueta,
  valor,
  detalle,
  anterior,
  media: mediaTres,
  formatoMedia,
  subirEsBueno = true,
}: {
  etiqueta: string;
  valor: string;
  detalle?: string;
  anterior: { valor: number; unidad: "%" | "pts" } | null;
  media: number | null;
  formatoMedia?: (n: number | null) => string;
  subirEsBueno?: boolean;
}) => {
  const translate = useTranslate();
  const bueno = anterior ? anterior.valor > 0 === subirEsBueno : null;
  const Icono = anterior && anterior.valor < 0 ? ArrowDownRight : ArrowUpRight;
  return (
    <div className="superficie flex flex-col justify-between gap-2.5 p-4">
      <p className="etiqueta min-h-[2.4em] leading-[1.2]">{etiqueta}</p>
      <p className="cifra-grande text-[1.75rem] leading-none">{valor}</p>
      <div className="space-y-0.5 text-xs">
        {anterior && anterior.valor !== 0 ? (
          <p
            className={cn(
              "inline-flex items-center gap-0.5 font-medium tabular-nums",
              bueno
                ? "text-emerald-700 dark:text-emerald-400"
                : "text-red-700 dark:text-red-400",
            )}
          >
            <Icono className="size-3.5" aria-hidden />
            {anterior.valor > 0 ? "+" : ""}
            {anterior.valor} {anterior.unidad}{" "}
            <span className="font-normal text-muted-foreground">
              {translate("nevora.informe.vs_anterior")}
            </span>
          </p>
        ) : anterior ? (
          <p className="text-muted-foreground">
            {translate("nevora.informe.igual")}
          </p>
        ) : null}
        {mediaTres != null && formatoMedia ? (
          <p className="text-muted-foreground">
            {translate("nevora.informe.media_tres", {
              valor: formatoMedia(mediaTres),
            })}
          </p>
        ) : null}
        {detalle ? <p className="text-muted-foreground">{detalle}</p> : null}
      </div>
    </div>
  );
};

const Conclusiones = ({ frases }: { frases: Conclusion[] }) => {
  const translate = useTranslate();
  if (!frases.length)
    return (
      <p className="text-sm text-muted-foreground">
        {translate("nevora.informe.conclusiones.pocos_datos")}
      </p>
    );
  return (
    <ul className="grid gap-2 md:grid-cols-2">
      {frases.map((f) => {
        const Icono =
          f.tono === "bien"
            ? TrendingUp
            : f.tono === "mal"
              ? TrendingDown
              : Info;
        return (
          <li
            key={f.texto}
            className="flex items-start gap-2.5 rounded-lg bg-foreground/[0.03] px-3 py-2.5 text-sm"
          >
            <Icono
              className={cn(
                "mt-0.5 size-4 shrink-0",
                f.tono === "bien"
                  ? "text-emerald-700 dark:text-emerald-400"
                  : f.tono === "mal"
                    ? "text-red-700 dark:text-red-400"
                    : "text-muted-foreground",
              )}
              aria-hidden
            />
            <span>{f.texto}</span>
          </li>
        );
      })}
    </ul>
  );
};

const PASOS_EMBUDO = [
  "entrados",
  "gestionados",
  "contactados",
  "interesados",
  "cita",
  "realizada",
  "propuesta",
  "cliente",
] as const;

/** Los leads que entraron en el mes y hasta dónde han llegado hoy, con el % de cada paso. */
const EmbudoMes = ({ fila }: { fila: MesInforme }) => {
  const translate = useTranslate();
  const total = fila.entrados;
  if (!total)
    return (
      <p className="text-sm text-muted-foreground">
        {translate("nevora.inicio.embudo.vacio")}
      </p>
    );
  return (
    <ol className="space-y-2">
      {PASOS_EMBUDO.map((paso, k) => {
        const n = fila[paso];
        const antes = k ? fila[PASOS_EMBUDO[k - 1]] : null;
        return (
          <li
            key={paso}
            className="grid grid-cols-[9rem_1fr_auto] items-center gap-3"
          >
            <span className="truncate text-sm">
              {translate(`nevora.inicio.embudo.pasos.${paso}`)}
            </span>
            <span className="relative h-6 overflow-hidden rounded-md bg-foreground/[0.04]">
              <span
                className="absolute inset-y-0 left-0 rounded-md bg-[var(--serie-llamadas)]"
                style={{
                  width: `${Math.max((n / total) * 100, n ? 1.5 : 0)}%`,
                }}
              />
            </span>
            <span className="flex w-24 items-baseline justify-end gap-2 tabular-nums">
              <span className="text-sm font-medium">{numero(n)}</span>
              <span className="w-10 text-right text-xs text-muted-foreground">
                {antes ? `${Math.round((n / antes) * 100)} %` : ""}
              </span>
            </span>
          </li>
        );
      })}
    </ol>
  );
};

/** Una fila por persona: actividad del mes y conversión (Jan y Robert). */
const EquipoMes = ({
  ranking,
  consultores,
  dias,
}: {
  ranking: DatosPanel["ranking"];
  consultores: DatosPanel["consultores"];
  dias: number;
}) => {
  const translate = useTranslate();
  const t = (k: string) => translate(`nevora.informe.equipo.${k}`);
  const p = (a: number, b: number) =>
    b > 0 ? `${Math.round((a / b) * 100)} %` : "—";
  if (!ranking.length && !consultores.length)
    return <p className="text-sm text-muted-foreground">{t("vacio")}</p>;
  return (
    <div className="space-y-5">
      {ranking.length ? (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left">
            <thead>
              <tr>
                <Th>{t("persona")}</Th>
                <Th derecha>{t("llamadas")}</Th>
                <Th derecha>{t("por_dia")}</Th>
                <Th derecha>{t("contacto")}</Th>
                <Th derecha>{t("agendadas")}</Th>
                <Th derecha>{t("hechas")}</Th>
                <Th derecha>{t("asistencia")}</Th>
                <Th derecha>{t("clientes")}</Th>
                <Th derecha>{t("cierre")}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {[...ranking]
                .sort((a, b) => b.clientes - a.clientes || b.citas - a.citas)
                .map((r) => (
                  <tr key={r.sale_id}>
                    <Td>{r.nombre}</Td>
                    <Td derecha>{numero(r.llamadas)}</Td>
                    <Td derecha>{decimal(dias ? r.llamadas / dias : null)}</Td>
                    <Td derecha>{p(r.contestadas, r.llamadas)}</Td>
                    <Td derecha>{numero(r.citas)}</Td>
                    <Td derecha>{numero(r.realizadas)}</Td>
                    <Td derecha>
                      {p(r.realizadas, r.realizadas + r.no_shows)}
                    </Td>
                    <Td derecha>{numero(r.clientes)}</Td>
                    <Td derecha>{p(r.clientes, r.realizadas)}</Td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {consultores.length ? (
        <div className="overflow-x-auto">
          <p className="etiqueta pb-2">{t("consultores")}</p>
          <table className="w-full min-w-[480px] text-left">
            <thead>
              <tr>
                <Th>{t("persona")}</Th>
                <Th derecha>{t("hechas")}</Th>
                <Th derecha>{t("asistencia")}</Th>
                <Th derecha>{t("clientes")}</Th>
                <Th derecha>{t("cierre")}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {consultores.map((c) => (
                <tr key={c.sale_id}>
                  <Td>{c.nombre}</Td>
                  <Td derecha>{numero(c.hechas)}</Td>
                  <Td derecha>{p(c.hechas, c.hechas + c.no_shows)}</Td>
                  <Td derecha>{numero(c.clientes)}</Td>
                  <Td derecha>{p(c.clientes, c.hechas)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
};

type SerieTendencia = {
  clave: string;
  valor: (m: MesInforme) => number | null;
  texto: (n: number | null) => string;
  soloAdmin?: boolean;
};

const SERIES: SerieTendencia[] = [
  { clave: "entrados", valor: (m) => m.entrados, texto: numero },
  {
    clave: "contacto",
    valor: (m) => pctONull(m.contestadas, m.llamadas),
    texto: porcentaje,
  },
  { clave: "realizadas", valor: (m) => m.realizadas, texto: numero },
  { clave: "clientes", valor: (m) => m.clientes, texto: numero },
  {
    clave: "cierre",
    valor: (m) => pctONull(m.clientes, m.realizadas),
    texto: porcentaje,
  },
  {
    clave: "cobrado",
    valor: (m) => m.cobrado,
    texto: importe,
    soloAdmin: true,
  },
];

/** Pequeños gráficos de 12 meses, uno por cifra, con el mes elegido resaltado. */
const Tendencia = ({
  filas,
  mes,
  esAdmin,
}: {
  filas: MesInforme[];
  mes: string;
  esAdmin: boolean;
}) => {
  const translate = useTranslate();
  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {SERIES.filter((s) => !s.soloAdmin || esAdmin).map((s) => {
        const ultima = filas[filas.length - 1];
        return (
          <div
            key={s.clave}
            className="rounded-lg bg-foreground/[0.03] px-4 py-3"
          >
            <div className="flex items-baseline justify-between gap-3">
              <p className="etiqueta">
                {translate(`nevora.informe.tendencia.series.${s.clave}`)}
              </p>
              <p className="text-base font-medium tabular-nums">
                {ultima ? s.texto(s.valor(ultima)) : "—"}
              </p>
            </div>
            <MiniBarras
              valores={filas.map((f) => ({
                mes: f.mes.slice(0, 10),
                v: s.valor(f),
                texto: s.texto(s.valor(f)),
              }))}
              mes={mes}
            />
          </div>
        );
      })}
    </div>
  );
};

/** Barras de 12 meses (la del mes elegido, en tinta llena); cada barra dice su valor al pasar. */
const MiniBarras = ({
  valores,
  mes,
}: {
  valores: { mes: string; v: number | null; texto: string }[];
  mes: string;
}) => {
  const max = Math.max(1, ...valores.map((x) => x.v ?? 0));
  const alto = 44;
  return (
    <svg
      viewBox={`0 0 ${valores.length * 12} ${alto + 12}`}
      className="mt-2 h-14 w-full"
      preserveAspectRatio="none"
      role="img"
      aria-label={valores
        .map((x) => `${nombreMes(x.mes)}: ${x.texto}`)
        .join("; ")}
    >
      {valores.map((x, k) => {
        const h = x.v ? Math.max((x.v / max) * alto, 2) : 0;
        return (
          <g key={x.mes}>
            <title>{`${nombreMes(x.mes)}: ${x.texto}`}</title>
            <rect
              x={k * 12 + 2}
              y={alto - h}
              width={8}
              height={h}
              rx={2}
              fill="var(--serie-llamadas)"
              opacity={x.mes === mes ? 1 : 0.35}
            />
            {x.v == null || x.v === 0 ? (
              <rect
                x={k * 12 + 2}
                y={alto - 1}
                width={8}
                height={1}
                fill="currentColor"
                opacity={0.15}
              />
            ) : null}
            <text
              x={k * 12 + 6}
              y={alto + 10}
              textAnchor="middle"
              className="fill-muted-foreground"
              style={{ fontSize: 7 }}
            >
              {new Date(`${x.mes}T12:00:00`)
                .toLocaleDateString("es-ES", { month: "narrow" })
                .toUpperCase()}
            </text>
          </g>
        );
      })}
    </svg>
  );
};

/** La tabla de siempre, mes a mes (del más reciente al más antiguo), con flechas. */
const TablaMesAMes = ({
  filas,
  esAdmin,
}: {
  filas: MesInforme[];
  esAdmin: boolean;
}) => {
  const translate = useTranslate();
  const t = (k: string) => translate(`nevora.informe.${k}`);
  const columnas = COLUMNAS.filter((c) => !c.soloAdmin || esAdmin);
  return (
    <div className="overflow-x-auto rounded-lg border border-border/60">
      <table className="w-full min-w-[960px] text-left">
        <thead>
          <tr className="border-b border-border/60">
            <th
              rowSpan={2}
              className="sticky left-0 z-10 bg-card px-4 pb-2 pt-4 text-xs font-medium text-muted-foreground"
            >
              {t("columnas.mes")}
            </th>
            {(["leads", "actividad", "dinero"] as const)
              .map(
                (g) =>
                  [g, columnas.filter((c) => c.grupo === g).length] as const,
              )
              .filter(([, n]) => n > 0)
              .map(([g, n]) => (
                <th
                  key={g}
                  colSpan={n}
                  className="etiqueta border-l border-border/60 px-3 pt-4 text-left"
                >
                  {t(`grupos.${g}`)}
                </th>
              ))}
          </tr>
          <tr className="border-b border-border/60">
            {columnas.map((c, k) => (
              <th
                key={c.clave}
                className={cn(
                  "px-3 pb-2 pt-1.5 text-right text-xs font-medium text-muted-foreground",
                  k > 0 &&
                    columnas[k - 1].grupo !== c.grupo &&
                    "border-l border-border/60",
                  c.clave === "motivo_principal" && "text-left",
                )}
              >
                {t(`columnas.${c.clave}`)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {filas.map((m, fila) => {
            const anterior = filas[fila + 1];
            return (
              <tr key={m.mes} className="hover:bg-foreground/[0.02]">
                <th
                  scope="row"
                  className="sticky left-0 z-10 whitespace-nowrap bg-card px-4 py-2.5 text-left text-sm font-medium"
                >
                  {nombreMes(m.mes)}
                </th>
                {columnas.map((c, k) => (
                  <Celda
                    key={c.clave}
                    borde={k > 0 && columnas[k - 1].grupo !== c.grupo}
                    izquierda={c.clave === "motivo_principal"}
                    flecha={
                      c.flecha && anterior ? (
                        <Flecha
                          actual={c.valor(m)}
                          antes={c.valor(anterior)}
                          subirEsBueno={c.subirEsBueno !== false}
                        />
                      ) : null
                    }
                  >
                    {c.texto(m)}
                  </Celda>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const Celda = ({
  children,
  flecha,
  borde,
  izquierda,
}: {
  children: ReactNode;
  flecha?: ReactNode;
  borde?: boolean;
  izquierda?: boolean;
}) => (
  <td
    className={cn(
      "px-3 py-2.5 text-sm tabular-nums",
      izquierda ? "max-w-[14rem] truncate text-left" : "text-right",
      borde && "border-l border-border/60",
    )}
  >
    <span className="inline-flex items-center gap-1">
      {children}
      {flecha}
    </span>
  </td>
);

/** Sube o baja frente al mes anterior (sin flecha si no hay con qué comparar). */
const Flecha = ({
  actual,
  antes,
  subirEsBueno,
}: {
  actual: number | null;
  antes: number | null;
  subirEsBueno: boolean;
}) => {
  if (actual == null || antes == null || actual === antes) return null;
  const sube = actual > antes;
  const bueno = sube === subirEsBueno;
  const Icono = sube ? ArrowUpRight : ArrowDownRight;
  return (
    <Icono
      className={cn(
        "size-3.5",
        bueno
          ? "text-emerald-700 dark:text-emerald-400"
          : "text-red-700 dark:text-red-400",
      )}
      aria-label={sube ? "sube" : "baja"}
    />
  );
};
